import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import '../styles/SiteDetail.css';

function getStatusInfo(status) {
  switch (status) {
    case 'active':   return { dot: '🟢', label: 'Active' };
    case 'trial':    return { dot: '🟢', label: 'Trial' };
    case 'warning':  return { dot: '🟡', label: 'Warning' };
    case 'expired':
    case 'archived': return { dot: '🔴', label: status.charAt(0).toUpperCase() + status.slice(1) };
    default:         return { dot: '⚪', label: status || 'Unknown' };
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ── Collapsible section (same pattern as DomainDetail) ────────────────────── */

function CollapsibleSection({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="site-section">
      <button className="site-section-header" onClick={() => setOpen(!open)}>
        <span className="site-section-chevron">{open ? '▼' : '▶'}</span>
        <span className="site-section-title">{title}</span>
      </button>
      {open && <div className="site-section-body">{children}</div>}
    </div>
  );
}

/* ── Dual bar chart: pageviews + visitors ─────────────────────────────────── */

function TrafficChart({ daily }) {
  if (!daily || daily.length === 0) return null;

  const maxVal = Math.max(...daily.map((d) => d.pageviews || 0), 1);

  return (
    <div className="traffic-chart">
      <div className="traffic-bars">
        {daily.map((d, i) => {
          const pvPct = ((d.pageviews || 0) / maxVal) * 100;
          const visPct = ((d.visitors || 0) / maxVal) * 100;
          const dayLabel = d.date
            ? new Date(d.date + 'T00:00:00Z').toLocaleDateString('en-US', { weekday: 'short' })
            : '';
          return (
            <div
              className="traffic-bar-col"
              key={i}
              title={`${dayLabel}: ${d.pageviews || 0} pageviews, ${d.visitors || 0} visitors`}
            >
              <div className="traffic-bar-pair">
                <div className="traffic-bar-fill traffic-bar-pageviews" style={{ height: `${pvPct}%` }} />
                <div className="traffic-bar-fill traffic-bar-visitors" style={{ height: `${visPct}%` }} />
              </div>
              <span className="traffic-bar-label">{dayLabel}</span>
            </div>
          );
        })}
      </div>
      <div className="traffic-legend">
        <span className="traffic-legend-item">
          <span className="traffic-legend-dot traffic-bar-pageviews" /> Pageviews
        </span>
        <span className="traffic-legend-item">
          <span className="traffic-legend-dot traffic-bar-visitors" /> Visitors
        </span>
      </div>
    </div>
  );
}

/* ── Top pages list ───────────────────────────────────────────────────────── */

function TopPagesList({ pages }) {
  if (!pages || pages.length === 0) {
    return <p className="site-muted">No page data yet</p>;
  }

  return (
    <div className="analytics-list">
      {pages.map((p, i) => (
        <div className="analytics-list-row" key={i}>
          <span className="analytics-list-path">{p.path}</span>
          <span className="analytics-list-count">{p.pageviews.toLocaleString()} views</span>
        </div>
      ))}
    </div>
  );
}

/* ── Top referrers list ───────────────────────────────────────────────────── */

function TopReferrersList({ referrers }) {
  if (!referrers || referrers.length === 0) {
    return <p className="site-muted">No referrer data yet</p>;
  }

  return (
    <div className="analytics-list">
      {referrers.map((r, i) => (
        <div className="analytics-list-row" key={i}>
          <span className="analytics-list-path">{r.referrer}</span>
          <span className="analytics-list-count">{r.visitors.toLocaleString()} visitors</span>
        </div>
      ))}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */

export default function SiteDetail({ site, onGetDomain }) {
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  useEffect(() => {
    if (!site) return;
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    setAnalytics(null);

    api.get(`/api/customer/sites/${site.id}/analytics`)
      .then((data) => {
        setAnalytics(data);
        if (data.error) setAnalyticsError(data.error);
      })
      .catch((err) => {
        console.error('Analytics fetch failed:', err);
        setAnalyticsError('Traffic data unavailable');
      })
      .finally(() => setAnalyticsLoading(false));
  }, [site?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!site) {
    return (
      <div className="site-detail-empty">
        <p>Select a site from the list to view its details.</p>
      </div>
    );
  }

  const status = getStatusInfo(site.hosting_status);
  const visitUrl = site.custom_domain
    ? `https://${site.custom_domain}`
    : site.url;
  const hasDomain = !!site.custom_domain;

  const analyticsNotConfigured = analyticsError === 'Analytics not configured for this site';

  return (
    <div className="site-detail">
      {/* Header */}
      <div className="site-detail-header">
        <div className="site-detail-title-row">
          <h2 className="site-detail-name">{site.custom_domain || site.site_id}</h2>
          <span className="site-detail-status">{status.dot} {status.label}</span>
        </div>
        <p className="site-detail-subtitle">Deployed {formatDate(site.deployed_at)}</p>
        <div className="site-detail-actions">
          {visitUrl && (
            <a href={visitUrl} target="_blank" rel="noopener noreferrer" className="site-action-btn site-action-primary">
              Visit Site →
            </a>
          )}
          {!hasDomain && onGetDomain && (
            <button className="site-action-btn site-action-secondary" onClick={() => onGetDomain(site.site_id, site.conversation_id)}>
              Get a Domain →
            </button>
          )}
        </div>
      </div>

      {/* Traffic */}
      <CollapsibleSection title="Traffic (Last 7 Days)" defaultOpen={true}>
        {analyticsLoading ? (
          <p className="site-muted">Loading traffic data...</p>
        ) : analyticsNotConfigured ? (
          <p className="site-muted">Analytics will be available after the next deploy</p>
        ) : analyticsError && !analytics?.daily?.length ? (
          <p className="site-muted">{analyticsError}</p>
        ) : analytics?.daily?.length ? (
          <>
            <TrafficChart daily={analytics.daily} />
            <p className="traffic-summary">
              {analytics.total_pageviews.toLocaleString()} pageview{analytics.total_pageviews !== 1 ? 's' : ''}
              {' · '}
              {analytics.total_visitors.toLocaleString()} visitor{analytics.total_visitors !== 1 ? 's' : ''}
            </p>
          </>
        ) : (
          <p className="site-muted">No traffic data yet</p>
        )}
      </CollapsibleSection>

      {/* Top Pages */}
      <CollapsibleSection title="Top Pages" defaultOpen={true}>
        {analyticsLoading ? (
          <p className="site-muted">Loading...</p>
        ) : analyticsNotConfigured ? (
          <p className="site-muted">Analytics will be available after the next deploy</p>
        ) : (
          <TopPagesList pages={analytics?.top_pages} />
        )}
      </CollapsibleSection>

      {/* Top Referrers */}
      <CollapsibleSection title="Top Referrers" defaultOpen={false}>
        {analyticsLoading ? (
          <p className="site-muted">Loading...</p>
        ) : analyticsNotConfigured ? (
          <p className="site-muted">Analytics will be available after the next deploy</p>
        ) : (
          <TopReferrersList referrers={analytics?.top_referrers} />
        )}
      </CollapsibleSection>

      {/* Hosting Details */}
      <CollapsibleSection title="Hosting Details" defaultOpen={true}>
        <div className="site-detail-grid">
          <div className="site-detail-row">
            <span className="site-detail-label">Status</span>
            <span className="site-detail-value">
              {site.hosting_status === 'trial' && site.trial_days_remaining != null
                ? `Trial (${site.trial_days_remaining} days remaining)`
                : status.label}
            </span>
          </div>
          {site.trial_started_at && (
            <div className="site-detail-row">
              <span className="site-detail-label">Trial started</span>
              <span className="site-detail-value">{formatDate(site.trial_started_at)}</span>
            </div>
          )}
          <div className="site-detail-row">
            <span className="site-detail-label">Preview URL</span>
            <span className="site-detail-value">
              <a href={site.url} target="_blank" rel="noopener noreferrer" className="site-inline-link">
                {site.url?.replace(/^https?:\/\//, '')} →
              </a>
            </span>
          </div>
          <div className="site-detail-row">
            <span className="site-detail-label">Custom domain</span>
            <span className="site-detail-value">
              {hasDomain ? (
                site.custom_domain
              ) : onGetDomain ? (
                <button className="site-get-domain-inline" onClick={() => onGetDomain(site.site_id, site.conversation_id)}>
                  None — Get a Domain →
                </button>
              ) : (
                'None'
              )}
            </span>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
}
